/**
 * ResultsPage — Muestra los resultados del scan: campos, verify e imágenes.
 * Ruta: /finalizado
 */
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { DocumentScanResult } from '@dicio/sdk-ocr-web-module';

import { AppHeader, Card } from '../../shared/components';
import { applyTheme, getParam } from '../../shared/theme';
import { ROUTES }          from '../../config/routes';
import { useDocumentImages } from './useDocumentImages';
import { useVerify }         from './useVerify';
import { FieldGroup }        from './components/FieldGroup';
import { DocumentImages }    from './components/DocumentImages';
import { VerifyCard }        from './components/VerifyCard';
import { JsonModal }         from './components/JsonModal';
import type { ResultsLocationState } from '../capture/types';

// ─── Helpers para agrupar los campos de DocumentScanResult ────────────────────

type Field = { label: string; value: string };

function field(label: string, value: string | undefined): Field | null {
  return value ? { label, value } : null;
}

function personalFields(d: DocumentScanResult): Field[] {
  return [
    field('Nombre completo',   d.nombreCompleto),
    field('Nombre(s)',         d.nombre),
    field('Apellido(s)',       d.apellidos),
    field('Apellido paterno',  d.apellidoPaterno),
    field('Apellido materno',  d.apellidoMaterno),
    field('Nombre de soltera', d.nombreSoltera),
    field('Sexo',              d.sexo),
    field('Fecha de nacimiento', d.fechaNacimiento),
    field('Lugar de nacimiento', d.lugarNacimiento),
    field('Nacionalidad',      d.nacionalidad),
    field('País',              d.codigoPais),
    field('Número de seguro social', d.numeroSeguroSocial),
    field('Número de certificado',   d.numeroCertificado),
    field('Estado civil',      d.estadoCivil),
    field('Profesión',         d.profesion),
    field('Empleador',         d.empleador),
  ].filter(Boolean) as Field[];
}

function documentFields(d: DocumentScanResult): Field[] {
  return [
    field('Número de documento',  d.numeroDocumento),
    field('Número adicional',     d.numeroDocumentoAdicional),
    field('Número personal',      d.numeroPersonal),
    field('Fecha de vencimiento', d.fechaVencimiento),
    field('Fecha de emisión',     d.fechaEmision),
    field('Fecha de entrada',     d.fechaEntrada),
    field('Vencimiento permanente', d.vencimientoPermanente ? 'Sí' : undefined),
    field('Autoridad emisora',    d.infoDocumento?.autoridad),
    field('Tipo de visa',         d.tipoVisa),
    field('Tipo de permiso de residencia', d.tipoPermisoResidencia),
  ].filter(Boolean) as Field[];
}

function contactFields(d: DocumentScanResult): Field[] {
  return [
    field('Dirección',          d.direccion),
    field('Dirección adicional', d.direccionAdicional),
    field('Municipio',          d.municipio),
  ].filter(Boolean) as Field[];
}

// ─── Componente ───────────────────────────────────────────────────────────────
export const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dataOtorgante, scanResult, rawResult, formValues } =
    (location.state as ResultsLocationState) ?? {};

  useEffect(() => {
    if (dataOtorgante?.length) applyTheme(dataOtorgante);
  }, [dataOtorgante]);

  const logoSrc = getParam(dataOtorgante ?? [], 'LOGO_OTORGANTE');
  const data    = scanResult as DocumentScanResult | undefined;

  const images      = useDocumentImages(rawResult);
  const verifyResult = useVerify({
    frontPhoto: images.front,
    backPhoto:  images.back,
    options:    formValues as Record<string, unknown> | undefined,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader logoSrc={logoSrc || undefined} />

      <main className="mx-auto max-w-3xl space-y-5 px-4 py-6">

        {!data ? (
          <Card>
            <p className="text-sm text-gray-500">No se obtuvo información del documento.</p>
          </Card>
        ) : (
          <>
            <FieldGroup title="Datos personales" fields={personalFields(data)}  columns={3} />
            <FieldGroup title="Documento"        fields={documentFields(data)}  columns={3} />
            <FieldGroup title="Domicilio"        fields={contactFields(data)}   columns={2} />
            {verifyResult && <VerifyCard result={verifyResult} />}
            <DocumentImages images={images} />
          </>
        )}

        <div className="flex flex-col gap-3 pb-8 pt-2">
          <JsonModal data={data} />
          <button
            className="btn-primary w-full"
            onClick={() => navigate(ROUTES.home, { state: { dataOtorgante } })}
          >
            Finalizar
          </button>
        </div>

      </main>
    </div>
  );
};
